import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import PropTypes from "prop-types";
import _ from "lodash";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import { Row, Text, Col, Div } from "../../../styles/Common";
import { theme } from "../../../styles/theme";
import { Button } from "../../../components/Button";
import { Select } from "../../../components/Select";
import { Input } from "../../../components/Input";
import { Radio } from "../../../components/Radio";
import { Tag } from "../../../components/Tag";
import useSaveUserProfile from "../hooks/useSaveUserProfile";
import useGetIncomeRange from "../../../core/hooks/useGetIncomeRange";
import useGetNumberChildren from "../../../core/hooks/useGetNumberChildren";
import useGetPhysicalActivities from "../../../core/hooks/useGetPhysicalActivities";
import useGetPets from "../../../core/hooks/useGetPets";
import useGetHobbies from "../../../core/hooks/useGetHobbies";
import useGetProfessions from "../../../core/hooks/useGetProfessions";
import { getLabelById, SPECIAL_HOBBY_ID, SPECIAL_PETS_ID } from "../../../utilities/helpers";
import ModalSuccess from "./ModalSuccess";
import { Instagram } from "../../../assets/icons";
import useGetLevelAcademic from "../../../core/hooks/useGetLevelAcademic";
import { yupResolver } from "@hookform/resolvers/yup";
import { validateProfileInfoStep3 } from "../../../utilities/validations";

export const Step3 = ({ formStep2, isMobile, formStep3 }) => {
  const queryClient = useQueryClient();
  const saveUserProfile = useSaveUserProfile();
  const { 
    handleSubmit, 
    control, 
    watch, 
    reset, 
    setValue, 
    formState: { errors } 
  } = useForm({resolver: yupResolver(validateProfileInfoStep3)});
  const [showModal, setShowModal] = useState(false);

  const selectedHobbies = watch("hobbies") || [];
  const hasSpecialHobby = selectedHobbies.includes(SPECIAL_HOBBY_ID);
  useEffect(() => {
    if (hasSpecialHobby && selectedHobbies.length > 1) {
      setValue("hobbies", [SPECIAL_HOBBY_ID]);
    }
  }, [hasSpecialHobby, selectedHobbies, setValue]);

  const selectedPets = watch("pets") || [];
  const hasSpecialPets = selectedPets.includes(SPECIAL_PETS_ID);
  useEffect(() => {
    if (hasSpecialPets && selectedPets.length > 1) {
      setValue("pets", [SPECIAL_PETS_ID]);
    }
  }, [hasSpecialPets, selectedPets, setValue]);


  useEffect(() => {
    reset({
      incomeRange: formStep3.incomeRange,
      numberChildren: formStep3.numberChildren,
      physicalActivities: formStep3.physicalActivities,
      levelAcademic: formStep3.levelAcademic,
      pets: formStep3.pets,
      profession: formStep3.profession,
      instagram: formStep3.instagram,
      householdRole: formStep3.householdRole,
      isPregnant: formStep3.isPregnant,
      isInterestedTechnology: formStep3.isInterestedTechnology,
      isAlcoholConsume: formStep3.isAlcoholConsume,
      isTobaccoConsume: formStep3.isTobaccoConsume,
      hobbies: formStep3.hobbies,
    });
  }, [formStep3]);

  const getIncomeRange = useGetIncomeRange();
  const { data: dataIncomeRange, isSuccess: isSuccessIncomeRange } =
    getIncomeRange;
  const [optionsIncomeRange, setOptionsIncomeRange] = useState([]);
  useEffect(() => {
    if (isSuccessIncomeRange) {
      setOptionsIncomeRange(
        dataIncomeRange?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataIncomeRange, isSuccessIncomeRange]);

  const getNumberChildren = useGetNumberChildren();
  const { data: dataNumberChildren, isSuccess: isSuccessNumberChildren } =
    getNumberChildren;
  const [optionsNumberChildren, setOptionsNumberChildren] = useState([]);
  useEffect(() => {
    if (isSuccessNumberChildren) {
      setOptionsNumberChildren(
        dataNumberChildren?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataNumberChildren, isSuccessNumberChildren]);

  const getPhysicalActivities = useGetPhysicalActivities();
  const {
    data: dataPhysicalActivities,
    isSuccess: isSuccessPhysicalActivities,
  } = getPhysicalActivities;
  const [optionsPhysicalActivities, setOptionsPhysicalActivities] = useState(
    []
  );
  useEffect(() => {
    if (isSuccessPhysicalActivities) {
      setOptionsPhysicalActivities(
        dataPhysicalActivities?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataPhysicalActivities, isSuccessPhysicalActivities]);

  const getLevelAcademic = useGetLevelAcademic();
    const {
      data: dataLevelAcademic,
      isSuccess: isSuccessLevelAcademic,
    } = getLevelAcademic;
    const [optionsLevelAcademic, setOptionsLevelAcademic] = useState(
      []
    );
    useEffect(() => {
      if (isSuccessLevelAcademic) {
        setOptionsLevelAcademic(
          dataLevelAcademic?.data?.result.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
      }
    }, [dataLevelAcademic, isSuccessLevelAcademic]);

  const getPets = useGetPets();
  const { data: dataPets, isSuccess: isSuccessPets } = getPets;
  const [optionsPets, setOptionsPets] = useState([]);
  useEffect(() => {
    if (isSuccessPets) {
      setOptionsPets(
        dataPets?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataPets, isSuccessPets]);

  const getHobies = useGetHobbies();
  const { data: dataHobbies, isSuccess: isSuccessHobbies } = getHobies;
  const [optionsHobbies, setOptionsHobbies] = useState([]);
  useEffect(() => {
    if (isSuccessHobbies) {
      setOptionsHobbies(
        dataHobbies?.data?.result.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    }
  }, [dataHobbies, isSuccessHobbies]);

  const getProfessions = useGetProfessions();
  const { data: dataProfessions, isSuccess: isSuccessProfessions } =
    getProfessions;
  const [optionsProfessions, setOptionsProfessions] = useState([]);
  useEffect(() => {
    if (isSuccessProfessions) {
      const groupedProfessions = _.groupBy(dataProfessions.data.result, "code");
      const formattedOptions = Object.keys(groupedProfessions).map((code) => ({
        label: code,
        options: groupedProfessions[code].map((item) => ({
          label: item.name,
          value: item.id,
        })),
      }));

      setOptionsProfessions(formattedOptions);
    }
  }, [dataProfessions, isSuccessProfessions]);

  const handleClose = (removedTag, value) => {
    const currentValues = watch(value);
    const newHobbies = currentValues.filter((item) => item !== removedTag);

    setValue(value, newHobbies);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSave = (data) => {
    saveUserProfile.reset();
    saveUserProfile.mutate(
      {
        userId: Number(formStep3.userId),
        maritalStatusId: null,
        genderId: null,
        roleHouseId: null,
        cityId: null,
        address: null,
        stepNumber: 3,
        phoneNumber: null,
        name: null,
        email: null,
        lastName: null,
        birthDate: null,
        phoneNumberCode: null,
        departmentId: null,
        incomeRangeId: data.incomeRange,
        numberChildrenId: data.numberChildren,
        frequencyActivitiesPhysicalId: data.physicalActivities,
        levelAcademicId: data.levelAcademic,
        petsId: data.pets ? data.pets.filter((item) => item) : null,
        professionsId: data.profession,
        instagram: data.instagram,
        isBuyManagerHome: data.householdRole,
        isPregnant: data.isPregnant,
        isInterestedTechnology: data.isInterestedTechnology,
        isAlcoholConsume: data.isAlcoholConsume,
        isTobaccoConsume: data.isTobaccoConsume,
        hobbiesId: data.hobbies ? data.hobbies.filter((item) => item) : null,
        instruction: null,
        zone: null,
        exactAddress: null,
        latitude: null,
        longitude: null,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["getUserProfile"]);
          queryClient.invalidateQueries(["getValidateIsFilledProfile"]);
          setShowModal(true);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.title);
        },
      }
    );
  };

  return (
    <>
      <ModalSuccess
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        isMobile={isMobile}
      />
      <Row m="0 0 20px 0">
        <Text
          size={!isMobile ? theme.fonts.size.h6 : theme.fonts.size.default}
          color={theme.colors.white}
        >
          Esta información adicional y algunos datos son completamente opcional, más adelante
          puedes complementarla y/o editarla
        </Text>
      </Row>
      <Div gap="32px" m="0 0 32px 0" direction={!isMobile ? "row" : "column"}>
        <Controller
          name="incomeRange"
          control={control}
          render={({ field }) => (
            <Select
              label="Rango de ingresos"
              color={theme.colors.gray200}
              border={theme.colors.white200}
              width={!isMobile ? "315.95px" : "100%"}
              height="45px"
              background={theme.colors.blue100}
              options={optionsIncomeRange}
              {...field}
            />
          )}
        />
        <Controller
          name="numberChildren"
          control={control}
          render={({ field }) => (
            <Select
              label="Cantidad de hijos"
              color={theme.colors.gray200}
              border={theme.colors.white200}
              error={errors.numberChildren}
              helper={errors.numberChildren ? errors.numberChildren.message : ""}
              width={!isMobile ? "315.95px" : "100%"}
              height="45px"
              background={theme.colors.blue100}
              options={optionsNumberChildren}
              {...field}
            />
          )}
        />

        <Controller
          name="physicalActivities"
          control={control}
          render={({ field }) => (
            <Select
              label="Frecuencia actividades físicas"
              color={theme.colors.gray200}
              border={theme.colors.white200}
              error={errors.physicalActivities}
              helper={errors.physicalActivities ? errors.physicalActivities.message : ""}
              width={!isMobile ? "315.95px" : "100%"}
              height="45px"
              background={theme.colors.blue100}
              options={optionsPhysicalActivities}
              {...field}
            />
          )}
        />
      </Div>
      <Div gap="32px" m="0 0 32px 0" direction={!isMobile ? "row" : "column"}>
        <Controller
          name="profession"
          control={control}
          render={({ field }) => (
            <Select
              label="Profesión u oficio"
              color={theme.colors.gray200}
              border={theme.colors.white200}
              error={errors.profession}
              helper={errors.profession ? errors.profession.message : ""}
              width={!isMobile ? "315.95px" : "100%"}
              height="45px"
              background={theme.colors.blue100}
              options={optionsProfessions}
              showSearch
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              {...field}
            />
          )}
        />
        <Controller
          name="levelAcademic"
          control={control}
          render={({ field }) => (
            <Select
              label="Nivel académico"
              color={theme.colors.gray200}
              border={theme.colors.white200}
              error={errors.levelAcademic}
              helper={errors.levelAcademic ? errors.levelAcademic.message : ""}
              width={!isMobile ? "315.95px" : "100%"}
              height="45px"
              background={theme.colors.blue100}
              options={optionsLevelAcademic}
              {...field}
            />
          )}
        />
        <Controller
          name="instagram"
          control={control}
          render={({ field }) => (
            <Input
              label="Perfil de Instagram"
              sizeLabel={theme.fonts.size.default}
              colorLabel={theme.colors.gray200}
              color={theme.colors.gray200}
              border={theme.colors.white200}
              style={{ padding: "5px 12px" }}
              width={!isMobile ? "315.95px" : "100%"}
              height="45px"
              background={theme.colors.blue100}
              options={optionsProfessions}
              suffix={
                <Instagram
                  stroke={theme.colors.gray200}
                  width={"24px"}
                  height={"24px"}
                />
              }
              {...field}
            />
          )}
        />
      </Div>
      <Div
        m="0 0 32px 0"
        gap={!isMobile ? "70px" : "45px"}
        direction={!isMobile ? "row" : "column"}
      >
        <Col gap="4px" width="300px">
          <Text color={theme.colors.gray200}>
            {" "}
            ¿Te interesa la innovación tecnológica?
          </Text>
          <Controller
            name="isInterestedTechnology"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Radio.Group {...field} value={field.value} gap="48px">
                  <Radio
                    value={true}
                    label="Si"
                    background={theme.colors.blue100}
                  />
                  <Radio
                    value={false}
                    label="No"
                    background={theme.colors.blue100}
                  />
                </Radio.Group>
                {error && (
                  <Text size={theme.fonts.size.xs} color={theme.colors.red}>
                    {error.message}
                  </Text>
                )}
              </>
            )}
          />
        </Col>
        <Col gap="10px" width="300px">
          <Text color={theme.colors.gray200}>
            ¿Tomas el rol de las compras del hogar?
          </Text>
          <Controller
            name="householdRole"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Radio.Group {...field} value={field.value} gap="48px">
                  <Radio
                    value={true}
                    label="Si"
                    background={theme.colors.blue100}
                  />
                  <Radio
                    value={false}
                    label="No"
                    background={theme.colors.blue100}
                  />
                </Radio.Group>
                {error && (
                  <Text size={theme.fonts.size.xs} color={theme.colors.red}>
                    {error.message}
                  </Text>
                )}
              </>
            )}
          />
        </Col>
        <Col gap="4px" width="300px">
          <Text color={theme.colors.gray200}>{formStep2?.gender == 6? "¿Te encuentras embarazada?": "¿Tu pareja se encuentra embarazada?"}</Text>
          <Controller
            name="isPregnant"
            control={control}
            render={({ field }) => (
              <Radio.Group {...field} value={field.value} gap="48px">
                <Radio
                  value={true}
                  label="Si"
                  background={theme.colors.blue100}
                />
                <Radio
                  value={false}
                  label="No"
                  background={theme.colors.blue100}
                />
              </Radio.Group>
            )}
          />
        </Col>
      </Div>
      <Div
        m="0 0 32px 0"
        gap={!isMobile ? "70px" : "45px"}
        direction={!isMobile ? "row" : "column"}
      >
        <Col gap="4px" width="300px">
          <Text color={theme.colors.gray200}>¿Consumes nicotina?</Text>
          <Controller
            name="isTobaccoConsume"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Radio.Group {...field} value={field.value} gap="48px">
                  <Radio
                    value={true}
                    label="Si"
                    background={theme.colors.blue100}
                  />
                  <Radio
                    value={false}
                    label="No"
                    background={theme.colors.blue100}
                  />
                </Radio.Group>
                {error && (
                  <Text size={theme.fonts.size.xs} color={theme.colors.red}>
                    {error.message}
                  </Text>
                )}
              </>
            )}
          />
        </Col>
        <Col gap="4px" width="300px">
          <Text color={theme.colors.gray200}>¿Consumes alcohol?</Text>
          <Controller
            name="isAlcoholConsume"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Radio.Group {...field} value={field.value} gap="48px">
                  <Radio
                    value={true}
                    label="Si"
                    background={theme.colors.blue100}
                  />
                  <Radio
                    value={false}
                    label="No"
                    background={theme.colors.blue100}
                  />
                </Radio.Group>
                {error && (
                  <Text size={theme.fonts.size.xs} color={theme.colors.red}>
                    {error.message}
                  </Text>
                )}
              </>
            )}
          />
        </Col>
      </Div>
      <Div m="0 0 12px 0" gap="32px" direction={!isMobile ? "row" : "column"}>
        <Col width={!isMobile ? "315.95px" : "100%"}>
          <Controller
            name="pets"
            control={control}
            render={({ field }) => (
              <Select
                label="Mascotas"
                mode="multiple"
                color={theme.colors.gray200}
                border={theme.colors.white200}
                width={!isMobile ? "315.95px" : "100%"}
                height="45px"
                background={theme.colors.blue100}
                options={optionsPets.map(option => ({
                  ...option,
                  disabled: hasSpecialPets && option.value !== SPECIAL_PETS_ID
                }))}
                onChange={(value) => {
                  const addingSpecialPets = 
                    value.includes(SPECIAL_PETS_ID) && 
                    !selectedPets.includes(SPECIAL_PETS_ID);
                  
                  if (addingSpecialPets) {
                    field.onChange([SPECIAL_PETS_ID]);
                  } else {
                    field.onChange(value);
                  }
                }}
                value={null}
                tagRender={() => null}
                {...field}
                showSearch
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            )}
          />
          <Row
            style={{ flexWrap: "wrap" }}
            m={!isMobile ? "16px 0 36px 0" : "16px 0 10px 0"}
          >
            {selectedPets &&
              selectedPets.length > 0 &&
              selectedPets
                .filter((item) => item)
                .map((item, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => handleClose(item, "pets")}
                  >
                    {getLabelById(item, optionsPets)}
                  </Tag>
                ))}
          </Row>
        </Col>
        <Col>
          <Controller
            name="hobbies"
            control={control}
            render={({ field }) => (
              <Select
                label="Hobbies"
                mode="multiple"
                color={theme.colors.gray200}
                border={theme.colors.white200}
                width={!isMobile ? "315.95px" : "100%"}
                height="45px"
                background={theme.colors.blue100}
                error={errors.hobbies}
                helper={errors.hobbies ? errors.hobbies.message : ""}
                options={optionsHobbies.map(option => ({
                  ...option,
                  disabled: hasSpecialHobby && option.value !== SPECIAL_HOBBY_ID
                }))}
                onChange={(value) => {
                  const addingSpecialHobby = 
                    value.includes(SPECIAL_HOBBY_ID) && 
                    !selectedHobbies.includes(SPECIAL_HOBBY_ID);
                  
                  if (addingSpecialHobby) {
                    field.onChange([SPECIAL_HOBBY_ID]);
                  } else {
                    field.onChange(value);
                  }
                }}
                value={null}
                tagRender={() => null}
                showSearch
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                {...field}
              />
            )}
          />
          <Row style={{ flexWrap: "wrap" }} m="16px 0 36px 0">
            {selectedHobbies &&
              selectedHobbies.length > 0 &&
              selectedHobbies
                .filter((item) => item)
                .map((item, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => handleClose(item, "hobbies")}
                  >
                    {getLabelById(item, optionsHobbies)}
                  </Tag>
                ))}
          </Row>
        </Col>
      </Div>

      <form onSubmit={handleSubmit(handleSave)}>
        <Button
          width={"146px"}
          htmlType="submit"
          loading={saveUserProfile.isLoading}
        >
          Finalizar
        </Button>
      </form>
    </>
  );
};

Step3.propTypes = {
  formStep2: PropTypes.object,
  formStep3: PropTypes.object,
  setStepNumber: PropTypes.func,
  isMobile: PropTypes.bool,
};
